/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useEffect, useState } from 'react';
import { useFormulaListStore } from '../stores/formulaListStore';
import { FormulaFilters } from '../components/formulas/FormulaFilters';
import { FormulaTable } from '../components/formulas/FormulaTable';
import { Pagination } from '../components/formulas/Pagination';
import { BatchAssignBar } from '../components/formulas/BatchAssignBar';
import { ClaimBar } from '../components/formulas/ClaimBar';
import { FormulaDetailModal } from '../components/formulas/FormulaDetailModal';
import { PageHelp } from '../components/layout/PageHelp';
import { Tooltip } from '../components/layout/Tooltip';

export function FormulaListPage() {
  const { formulas, isLoading, error, loadFormulas, selectedSetIds, totalCount, searchText, searchMode, searchResultCount, claimedSetIds } = useFormulaListStore();
  const [openSetId, setOpenSetId] = useState<string | null>(null);

  useEffect(() => {
    loadFormulas();
  }, [loadFormulas]);

  function handleModalClose() {
    setOpenSetId(null);
    // Refresh list in case assignments changed
    loadFormulas();
  }

  const hasSearch = !!searchText?.trim();
  const matchLabel =
    searchMode === 'ingredient' ? 'ingredient matches'
    : searchMode === 'formula' ? 'formula matches'
    : 'matches';

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">Formulas</h2>
        <div className="flex items-center gap-3 text-sm text-gray-500">
          {selectedSetIds.size > 0 && (
            <Tooltip text="Number of formulas selected for batch assignment">
              <span>{selectedSetIds.size} selected</span>
            </Tooltip>
          )}
          {hasSearch && (
            <Tooltip text="Total formulas matching the current search">
              <span className="text-purple-600 font-medium">{searchResultCount} {matchLabel}</span>
            </Tooltip>
          )}
          <Tooltip text="Total formula count in the system">
            <span>{totalCount} total</span>
          </Tooltip>
        </div>
      </div>

      <PageHelp
        title="How to use the Formula Queue"
        steps={[
          'Use the Status tabs (All / Unassigned / Assigned) and the Dosage Form and Schedule dropdowns to narrow the list.',
          'Type in the search box to match either formula names or ingredient names — both are searched by default.',
          'Tick "Ingredient Only" to restrict the search to ingredients, or "Formula Only" to restrict it to formula names. Only one may be active; untick both to search both.',
          'Click the "Clear" button to reset all filters and search text in one click.',
          'Click any formula row to open the detail modal for single assignment.',
          'Check the boxes on unassigned formulas, then click "Batch Assign" to assign taxonomy codes to multiple formulas at once.',
        ]}
        tips={[
          'Filter by "Unassigned" status to focus on formulas needing work.',
          'After batch-assigning, the list auto-refreshes to reflect changes.',
          'Click "Select all" then "Expand to all matches" to select across all pages — the count matches the displayed total because the same status and filter set is applied.',
        ]}
      />

      {!claimedSetIds && <FormulaFilters />}

      <ClaimBar />

      <BatchAssignBar />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="text-center text-gray-400 py-12">Loading formulas...</div>
      ) : (
        <>
          <FormulaTable formulas={formulas} showCheckboxes={true} onFormulaClick={setOpenSetId} />
          {!claimedSetIds && <Pagination />}
        </>
      )}

      {openSetId && (
        <FormulaDetailModal setId={openSetId} onClose={handleModalClose} readOnly={selectedSetIds.size > 0} />
      )}
    </div>
  );
}
